// /privacy — generic draft. Needs legal review before claiming
// anything binding. Keep it short, honest, human-readable; the real
// legal doc can live elsewhere once signed off.
import { useSeo } from "@/hooks";
import { MarketingShell } from "./MarketingShell";

export function PrivacyPage() {
  useSeo({
    title: "Privacy — malbox",
    description: "What malbox stores, what it doesn't, and how long. No third-party analytics, no ad trackers, no sample bytes shared with external services.",
    path: "/privacy",
  });
  return (
    <MarketingShell
      eyebrow="Privacy"
      title="What we store and why."
      lede={<>Short version: samples you upload, metadata about them, your account identifiers if you sign in. Nothing else. We don't sell data and don't carry third-party ad trackers.</>}
    >
      <div className="max-w-[720px] space-y-8 text-ui text-ink-soft">
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>What we collect</h2>
          <ul className="mt-3 list-inside list-disc space-y-1 pl-2">
            <li>Samples you submit (the bytes, your filename, your chosen profile and timeout)</li>
            <li>The analysis output we produce — telemetry events, pcap, memory dumps, screenshots, reports</li>
            <li>If you sign in: your GitHub user id and display name</li>
            <li>Request metadata used for abuse mitigation (IP, user agent, timestamps) — retained 30 days</li>
          </ul>
        </section>
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>What we don't collect</h2>
          <ul className="mt-3 list-inside list-disc space-y-1 pl-2">
            <li>No third-party analytics, no cookies for tracking, no ad network pixels</li>
            <li>No third-party ad trackers or marketing pixels anywhere on the site</li>
            <li>We don't read, repackage, or sell your samples to other services</li>
          </ul>
        </section>
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>Who sees your submissions</h2>
          <p className="mt-3">
            On the hosted service: by default your submissions are private to your account. You can opt-in per submission to publish your display name alongside the sample metadata. Sample bytes themselves are never auto-shared.
          </p>
          <p className="mt-3">
            Third-party intel lookups (VirusTotal, Triage, Hybrid Analysis, MalwareBazaar, YARAify, MalShare) are queried by sha256 only — we never send the sample bytes. Those lookups are opt-in per deployment.
          </p>
        </section>
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>Retention</h2>
          <ul className="mt-3 list-inside list-disc space-y-1 pl-2">
            <li>Sample bytes: retained indefinitely (sha256-addressed storage)</li>
            <li>Analysis artifacts (pcap, memdump, screenshots): 60 days on the hosted service</li>
            <li>Metadata / jobs rows: retained for audit</li>
            <li>Request logs: 30 days</li>
          </ul>
        </section>
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>Your rights</h2>
          <p className="mt-3">
            Delete your account and any associated submissions by emailing <a className="text-ink underline" href="mailto:neel@neels.dev">neel@neels.dev</a>. We'll confirm within 7 days.
          </p>
        </section>
        <section>
          <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>Changes</h2>
          <p className="mt-3">
            If this policy materially changes we'll note it in the <a className="text-ink underline" href="/changelog">changelog</a> and date the update.
          </p>
        </section>
        <p className="pt-6 meta tabular-nums">Last updated 2026-10-24.</p>
      </div>
    </MarketingShell>
  );
}
